import { useState } from "react";
import { Tag as TagIcon, Plus } from "lucide-react";
import { Button } from "@/components/ui";
import { useTranslation } from "@/packages/utils";
import { TagList, TagFormDialog, TagDeleteDialog } from "@/components/ui/Tag";
import { SettingsPageHeader, SettingsPanel } from "./SettingsSection";
import {
  useAuditTags,
  useAuditTagUsageCounts,
  useCreateAuditTag,
  useUpdateAuditTag,
  useDeleteAuditTag,
} from "@/hooks/use-audit-tags";
import type { Tag } from "@/components/ui/Tag/types";
import type { Id } from "@/packages/types";

export function SettingsAuditTagsSection() {
  const { t } = useTranslation(["settings"]);
  const { tags, isLoading: isTagsLoading } = useAuditTags();
  const { usageMap, isLoading: isUsageLoading } = useAuditTagUsageCounts();
  const createTag = useCreateAuditTag();
  const updateTag = useUpdateAuditTag();
  const deleteTag = useDeleteAuditTag();
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingTag, setDeletingTag] = useState<Tag | null>(null);

  const isLoading = isTagsLoading || isUsageLoading;

  const handleCreate = () => {
    setEditingTag(null);
    setFormDialogOpen(true);
  };

  const handleEdit = (tag: Tag) => {
    setEditingTag(tag);
    setFormDialogOpen(true);
  };

  const handleDelete = (tag: Tag) => {
    setDeletingTag(tag);
    setDeleteDialogOpen(true);
  };

  const handleFormSubmit = async (data: { name: string; color: string }) => {
    if (editingTag) {
      await updateTag({ id: editingTag.id as Id<"auditTags">, ...data });
    } else {
      await createTag(data);
    }
  };

  const handleDeleteConfirm = async () => {
    if (deletingTag) {
      await deleteTag({ id: deletingTag.id as Id<"auditTags"> });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-sm text-muted">{t("settings:auditTags.loading")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <SettingsPageHeader
        eyebrow={t("settings:sections.organization")}
        title={t("settings:auditTags.title")}
        description={t("settings:auditTags.description")}
        action={
          <Button variant="primary" icon={Plus} onClick={handleCreate}>
            {t("settings:auditTags.addTag")}
          </Button>
        }
      />
      <SettingsPanel>
        {tags && tags.length > 0 ? (
          <TagList tags={tags} onEdit={handleEdit} onDelete={handleDelete} />
        ) : (
          <div className="flex flex-col items-center justify-center h-48 rounded-xl border border-dashed border-border">
            <TagIcon className="h-8 w-8 text-muted mb-3" />
            <p className="text-sm text-muted">
              {t("settings:auditTags.empty")}
            </p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-3"
              onClick={handleCreate}
            >
              {t("settings:auditTags.createFirst")}
            </Button>
          </div>
        )}

        <TagFormDialog
          open={formDialogOpen}
          onClose={() => setFormDialogOpen(false)}
          mode={editingTag ? "edit" : "create"}
          initialData={editingTag ?? undefined}
          onSubmit={handleFormSubmit}
        />

        <TagDeleteDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          tagName={deletingTag?.name ?? ""}
          usageCount={deletingTag ? usageMap.get(deletingTag.id) : undefined}
          onConfirm={handleDeleteConfirm}
        />
      </SettingsPanel>
    </div>
  );
}
